import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';


import { updateCustomTask } from '../../api';
import { createCustomTask } from '../../api/createCustomTask';
import { findKeyTabById } from '../../lib/getTabById';
import { getUpdatedTabs } from '../services/getUpdatedTabs';
import { Project } from '../types/tasks';
import { Tabs, TasksInformationSchema } from '../types/tasksInformationSchema';

const initialState: TasksInformationSchema = {
    tabs: {
        board: {
            outdated: [],
            no_term: [],
            in_progress: [],
            today: [],
            seven_days: [],
        },
        list: {
            no_term: [],
            completed: [],
        },
    },
    observers: [],
    executors: [],
    employees: [],
    projects: [],
    executorsForFilter: [],
    tags: [],
    roles: undefined,
    filteredTabs: null,
    isFiltered: false,
    isShowGanttTabs: false,
    isNeedFetchGant: true,
    isSorted: false,
    isShowCompleted: false,
    isCollapsedTabsInList: false,
    isExpandedTabsInList: false,
};

export const tasksInformation = createSlice({
    name: 'tasksInformation',
    initialState,
    reducers: {
        setTasksInformation: (
            state: TasksInformationSchema,
            action: PayloadAction<TasksInformationSchema>
        ) => {
            state.tabs = action.payload.tabs;
            state.roles = action.payload.roles;
            state.employees = action.payload.employees;
            state.tags = action.payload.tags;
            state.projects = action.payload.projects;
            state.projectsForFilter = action.payload.projectsForFilter;
            state.executorsForFilter = action.payload.executorsForFilter;
        },
        changeNeedGantt: (state: TasksInformationSchema) => {
            state.isNeedFetchGant = false;
        },
        updateProjectsForFilter: (
            state: TasksInformationSchema,
            action: PayloadAction<Project[]>
        ) => {
            state.projectsForFilter = action.payload;
        },
        setGanttTasksInformation: (
            state: TasksInformationSchema,
            action: PayloadAction<{ tabs: Tabs; projectsForFilter: Project[] }>
        ) => {
            state.tabs = action.payload.tabs;
            state.projectsForFilterGantt = action.payload.projectsForFilter;
        },
        changeShowCompleted: (state: TasksInformationSchema, action: PayloadAction<boolean>) => {
            state.isShowCompleted = action.payload;
        },
        changeSort: (state: TasksInformationSchema, action: PayloadAction<boolean>) => {
            const isSorted = action.payload;

            state.isSorted = isSorted;

            const list = { ...state.tabs.list };
            state.tabs.list = Object.keys(list)
                .sort((a, b) => {
                    const dateA = dayjs(a, 'YYYY-MM-DD').valueOf();
                    const dateB = dayjs(b, 'YYYY-MM-DD').valueOf();

                    return isSorted ? dateB - dateA : dateA - dateB;
                })
                .reduce((acc, key) => {
                    acc[key] = list[key];
                    return acc;
                }, {} as typeof list);
        },
        setShowGanttTabs: (state: TasksInformationSchema, action: PayloadAction<boolean>) => {
            state.isShowGanttTabs = action.payload;
        },
        changeFilteredBoard: (state, { payload }: PayloadAction<Tabs | null>) => {
            state.filteredTabs = payload;
        },
        updateTabs: (state, { payload }: PayloadAction<Tabs>) => {
            state.tabs = getUpdatedTabs(state.tabs, payload);
        },
        onDeleteTab: (state, { payload }: PayloadAction<string>) => {
            const foundTab = findKeyTabById(state.tabs.list, payload);
            const foundInBoard = findKeyTabById(state.tabs.board, payload);

            if (foundInBoard) {
                state.tabs.board[foundInBoard] = [...state.tabs.board[foundInBoard]].filter(
                    (uid) => uid !== payload
                );
            }

            state.tabs.list[foundTab] = state.tabs.list[foundTab].filter((id) => id !== payload);

            if (
                foundTab &&
                state.tabs.list[foundTab].length === 0 &&
                foundTab != 'completed' &&
                foundTab != 'no_term'
            ) {
                delete state.tabs.list[foundTab];
            }
        },
        collapseAllTabsInList: (state) => {
            state.isCollapsedTabsInList = !state.isCollapsedTabsInList;
            state.isExpandedTabsInList = false;
        },
        expandAllTabsInList: (state) => {
            state.isExpandedTabsInList = true;
            state.isCollapsedTabsInList = !state.isCollapsedTabsInList;
        },
        setIsFiltered: (state, { payload }: PayloadAction<boolean>) => {
            state.isFiltered = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createCustomTask.fulfilled, (state, action) => {
                state.tabs = getUpdatedTabs(state.tabs, action.payload.newTabs);
                state.tags = [...state.tags, ...action.payload.newTags];
            })
            .addCase(updateCustomTask.fulfilled, (state, action) => {
                state.tabs = getUpdatedTabs(state.tabs, action.payload.newTabs);
                const updatedTabs = getUpdatedTabs(state.tabs, action.payload.newTabs);
                if (updatedTabs != state.tabs) {
                    state.tabs = updatedTabs;
                }
                state.tags = [...state.tags, ...action.payload.newTags];
            });
    },
});

export const { actions: tasksInformationActions } = tasksInformation;
export const { reducer: tasksInformationReducer } = tasksInformation;
